import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

function KeywordDiscover() {
  const [seedInput, setSeedInput] = useState('');
  const [seeds, setSeeds] = useState([]);
  const [country, setCountry] = useState('Turkey');
  const [language, setLanguage] = useState('Turkish');
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [error, setError] = useState('');
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendData, setTrendData] = useState([]);

  const handleAddSeed = () => {
    const trimmed = (seedInput || '').trim();
    if (!trimmed) return;
    if (!seeds.includes(trimmed)) setSeeds((s) => [...s, trimmed]);
    setSeedInput('');
    discoverForSeed(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSeed();
    }
  };

  const removeSeed = (kw) => setSeeds((s) => s.filter((x) => x !== kw));

  const handleDiscover = async () => {
    if (seeds.length === 0) return;
    setIsDiscovering(true);
    setError('');
    setIdeas([]);

    const countryToGeo = {
      'Turkey': 'geoTargetConstants/2792',
      'United States': 'geoTargetConstants/2840',
      'United Kingdom': 'geoTargetConstants/2826',
      'Germany': 'geoTargetConstants/2276',
    };
    const languageToConst = {
      // send numeric IDs; backend/script will build the resource name
      'Turkish': '1037',
      'English': '1000',
      'German': '1005',
    };

    const geoTargetConstant = countryToGeo[country] || 'geoTargetConstants/2792';
    const languageConstant = languageToConst[language] || 'languageConstants/1000';

    try {
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/keyword-discover/suggestions', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          seedKeywords: seeds,
          languageConstant,
          geoTargetConstant,
        })
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Request failed';
        let details;
        if (ct.includes('application/json')) {
          const jsErr = await res.json().catch(() => ({}));
          msg = jsErr.error || msg;
          details = jsErr.details;
        }
        throw new Error(details ? `${msg}: ${typeof details === 'string' ? details : (details.stderr || details.error || '')}`.trim() : msg);
      }
      const js = await res.json();
      const list = Array.isArray(js?.ideas) ? js.ideas : [];
      setIdeas(list.slice(0, 75));
    } catch (e) {
      setError(e?.message || 'Failed to fetch suggestions');
    } finally {
      setIsDiscovering(false);
    }
  };

  const discoverForSeed = async (seedText) => {
    const s = (seedText || '').trim();
    if (!s) return;
    setIsDiscovering(true);
    setError('');
    setIdeas([]);

    const countryToGeo = {
      'Turkey': 'geoTargetConstants/2792',
      'United States': 'geoTargetConstants/2840',
      'United Kingdom': 'geoTargetConstants/2826',
      'Germany': 'geoTargetConstants/2276',
    };
    const languageToConst = {
      'Turkish': '1037',
      'English': '1000',
      'German': '1005',
    };

    const geoTargetConstant = countryToGeo[country] || 'geoTargetConstants/2792';
    const languageConstant = languageToConst[language] || 'languageConstants/1000';

    try {
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/keyword-discover/suggestions', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          seedKeywords: [s],
          languageConstant,
          geoTargetConstant,
        })
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Request failed';
        let details;
        if (ct.includes('application/json')) {
          const jsErr = await res.json().catch(() => ({}));
          msg = jsErr.error || msg;
          details = jsErr.details;
        }
        throw new Error(details ? `${msg}: ${typeof details === 'string' ? details : (details.stderr || details.error || '')}`.trim() : msg);
      }
      const js = await res.json();
      const list = Array.isArray(js?.ideas) ? js.ideas : [];
      setIdeas(list.slice(0, 75));
    } catch (e) {
      setError(e?.message || 'Failed to fetch suggestions');
    } finally {
      setIsDiscovering(false);
    }
  };

  const countryToGeo = {
    'Turkey': 'geoTargetConstants/2792',
    'United States': 'geoTargetConstants/2840',
    'United Kingdom': 'geoTargetConstants/2826',
    'Germany': 'geoTargetConstants/2276',
  };
  const languageToConst = {
    // send numeric IDs; backend/script will build the resource name
    'Turkish': '1037',
    'English': '1000',
    'German': '1005',
  };

  const fetchTrend = async (kw) => {
    if (!kw) return;
    setTrendLoading(true);
    setTrendData([]);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const geoTargetConstant = countryToGeo[country] || 'geoTargetConstants/2792';
      const languageConstVal = languageToConst[language] || '1000';
      const res = await fetch('https://api.glowytics.com/api/search-volume/monthly', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          keyword: kw,
          languageConstant: languageConstVal,
          geoTargetConstant,
        })
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Request failed';
        if (ct.includes('application/json')) {
          const jsErr = await res.json().catch(() => ({}));
          msg = jsErr.error || msg;
        }
        throw new Error(msg);
      }
      const js = await res.json();
      const monthly = Array.isArray(js?.monthly) ? js.monthly : [];
      const last12 = monthly.slice(-12).map((m) => ({
        name: `${String(m.month).padStart(2, '0')}/${m.year}`,
        volume: m.monthly_searches || 0,
      }));
      setTrendData(last12);
    } catch (e) {
      setTrendData([]);
    } finally {
      setTrendLoading(false);
    }
  };

  const now = useMemo(() => new Date(), []);
  const displayDateRange = useMemo(() => {
    const start = 'Jan 2022';
    const end = now.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    return `${start} – ${end}`;
  }, [now]);

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 gap-6">
        {/* Controls */}
        <div className="bg-white rounded-xl border p-5">
          {/* Header inside the control card */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img src="/discovergoogleads.svg" alt="Keyword Discover" className="w-20 h-20" />
              <div>
                <h2 className="text-2xl text-gray-800">Keyword Discover</h2>
                <p className="text-sm text-gray-500">Discover related keywords from your terms.</p>
              </div>
            </div>
            <div />
          </div>

          <div className="flex flex-col lg:flex-row lg:items-start gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={seedInput}
                  onChange={(e) => setSeedInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a keyword and press Enter"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddSeed}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add
                </button>
              </div>

              {seeds.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {seeds.map((kw) => (
                    <span key={kw} className="inline-flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                      <span>{kw}</span>
                      <button onClick={() => removeSeed(kw)} className="text-blue-500 hover:text-blue-700">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 w-full lg:w-auto self-start">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option>Turkey</option>
                  <option>United States</option>
                  <option>United Kingdom</option>
                  <option>Germany</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option>Turkish</option>
                  <option>English</option>
                  <option>German</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3" />
          </div>
        </div>

        {/* Results placeholder */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 items-start">
          <div className="xl:col-span-3 bg-white rounded-xl border p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium text-gray-800">Suggested Keywords</h3>
              <span className="text-xs text-gray-500">Google Ads Keyword Planner</span>
            </div>

            {isDiscovering ? (
              <div className="space-y-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : seeds.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-12 gap-3">
                <img src="/searchtrend.svg" alt="Discover" className="w-24 h-24" />
                <p className="text-gray-600">Add a seed keyword to see suggestions.</p>
              </div>
            ) : error ? (
              <div className="text-sm text-red-600">{error}</div>
            ) : ideas.length === 0 ? (
              <div className="text-sm text-gray-500">No suggestions returned.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="pb-2">Keyword</th>
                      <th className="pb-2">Avg Monthly Searches</th>
                      <th className="pb-2">Competition</th>
                    </tr>
                  </thead>
                  <tbody className="align-top">
                    {ideas.map((it, idx) => (
                      <tr
                        key={idx}
                        className={`cursor-pointer ${selectedKeyword === it.text ? 'bg-blue-50' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50')}`}
                        onClick={() => {
                          setSelectedKeyword(it.text);
                          fetchTrend(it.text);
                        }}
                      >
                        <td className="py-2 pr-4 text-gray-800">{it.text}</td>
                        <td className="py-2 pr-4 text-gray-700">{it.avgMonthlySearches ?? '-'}</td>
                        <td className="py-2 pr-4 text-gray-700">{it.competition || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border p-5 xl:col-span-2 self-start xl:sticky xl:top-24">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium text-gray-800">Trends (last 12 months)</h3>
              {selectedKeyword && <span className="text-xs text-gray-500">{selectedKeyword}</span>}
            </div>
            {trendLoading ? (
              <div className="h-56 bg-gray-50 border border-dashed border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-sm">Loading…</div>
            ) : trendData.length === 0 ? (
              <div className="h-56 bg-gray-50 border border-dashed border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-sm">
                {selectedKeyword ? 'No data' : 'Select a keyword to see trend'}
              </div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => [v, 'Searches']} />
                    <Legend />
                    <Line type="monotone" dataKey="volume" name="Searches" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default KeywordDiscover;


